import React, { useState, useEffect } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ImageUploader from "../../../../../../components/common/ImageUploader";
import ImagePreviewer from "../../../../../../components/common/ImagePreviewer";
import DeleteConfirmDialog from "../DeleteConfirmDialog";
import { ESRImagesProps, ImageAttachment } from "./utils/types";
import { useAuth } from "../../../../../../components/AuthProvider";

const ESRImages: React.FC<ESRImagesProps> = ({
  attachments,
  onAttachmentsChange,
  onDeleteExisting,
  images,
  onChange = () => {},
  disabled = false,
  confirmDelete = false,
}) => {
  const { currentUserRole } = useAuth() || {};
  const isEquipmentManager = currentUserRole?.name === "Equipment Manager";
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [isDeletionConfirmed, setIsDeletionConfirmed] = useState<boolean>(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  const [pendingAttachment, setPendingAttachment] = useState<ImageAttachment | null>(null);

  const isReadOnly = Boolean(disabled || isEquipmentManager);

  const count = attachments ? attachments.length : (images?.length || 0);
  const remaining = 8 - count;
  let countText = "";
  if (isReadOnly) {
    countText = `${count}/8 images`;
  } else if (remaining === 0) {
    countText = `${count}/8 – No slots remaining`;
  } else if (remaining === 1) {
    countText = `${count}/8 – 1 slot remaining`;
  } else {
    countText = `${count}/8 – ${remaining} slots remaining`;
  }

  const displayImages: (File | string)[] = attachments
    ? attachments.map((att) => att.file || att.url)
    : (images || []);

  const handleUploaderChange = (updatedFiles: (File | string)[]) => {
    if (attachments && onAttachmentsChange) {
      if (updatedFiles.length > attachments.length) {
        const newFiles = updatedFiles.slice(attachments.length) as File[];
        const newAttachments: ImageAttachment[] = newFiles.map((file) => ({
          id: `${file.name}-${file.lastModified}-${Date.now()}`,
          url: URL.createObjectURL(file),
          file: file,
          isExisting: false,
        }));
        onAttachmentsChange([...attachments, ...newAttachments]);
      }
    } else {
      onChange(updatedFiles);
    }
  };

  const handleRemoveRequest = (index: number) => {
    if (attachments && onAttachmentsChange) {
      const target = attachments[index];
      if (!target) return;

      if (confirmDelete && target.isExisting) {
        setPendingAttachment(target);
        setOpenDeleteDialog(true);
      } else {
        // Newly added local upload — discard immediately without confirmation modal
        onAttachmentsChange(attachments.filter((_, idx) => idx !== index));
      }
      return;
    }

    // Fallback for legacy images prop
    if (confirmDelete) {
      setPendingDeleteIndex(index);
      setOpenDeleteDialog(true);
    } else {
      const updated = (images || []).filter((_, idx) => idx !== index);
      onChange(updated);
    }
  };

  useEffect(() => {
    if (isDeletionConfirmed) {
      if (pendingAttachment && attachments && onAttachmentsChange) {
        onAttachmentsChange(attachments.filter((att) => att.id !== pendingAttachment.id));
        if (onDeleteExisting) {
          onDeleteExisting(pendingAttachment.id);
        }
        setPendingAttachment(null);
      } else if (pendingDeleteIndex !== null && images) {
        const updated = images.filter((_, idx) => idx !== pendingDeleteIndex);
        onChange(updated);
        setPendingDeleteIndex(null);
      }
      setIsDeletionConfirmed(false);
    }
  }, [
    isDeletionConfirmed,
    pendingAttachment,
    pendingDeleteIndex,
    attachments,
    onAttachmentsChange,
    onDeleteExisting,
    images,
    onChange,
  ]);

  return (
    <>
      <Stack direction="column" mb={2} mt={5} gap={2}>
        <Typography variant="h6" component="div">
          Images{" "}
          <Typography component="span" variant="body2">
            ({countText})
          </Typography>
        </Typography>

        {count === 0 && isReadOnly ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              bgcolor: "#fafafa",
              borderRadius: 2,
              border: "1px dashed #ccc",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">No image attachments available.</Typography>
          </Box>
        ) : (
          <ImageUploader
            images={displayImages}
            onChange={handleUploaderChange}
            multiple
            maxCount={8}
            disabled={isReadOnly}
            readOnly={isReadOnly}
            onRemoveRequest={handleRemoveRequest}
            onImageClick={(index) => {
              setViewerIndex(index);
              setViewerOpen(true);
            }}
          />
        )}
      </Stack>

      <ImagePreviewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        images={displayImages}
        initialIndex={viewerIndex}
      />

      <DeleteConfirmDialog
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
        setIsDeletionConfirmed={setIsDeletionConfirmed}
        type="image"
      />
    </>
  );
};

export default ESRImages;